import { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { deriveLifecycle } from '@shared/competitionRules';

// Use apiKey auth mode — this resolution runs before any sign-in decision
// is made, for both members and guests, matching SmartCheckInPage/
// GuestCatchSubmission's existing public Event reads.
const client = generateClient({ authMode: 'apiKey' });

export interface OpenCompetition {
  id: string;
  title: string;
  eventDate: string;
}

export interface UpcomingCompetition {
  id: string;
  title: string;
  eventDate: string;
  location?: string | null;
}

export interface UseActiveCompetitionResult {
  /** True while the initial Event query is in flight. */
  loading: boolean;
  /** More than one competition is currently open for this purpose — caller should show a picker. */
  openComps: OpenCompetition[];
  /** Exactly one competition is open for this purpose — this is its id. */
  activeEventId: string | null;
  /** No competition is open, but one is upcoming (sign-on/fishing not yet open, in the future). */
  nextComp: UpcomingCompetition | null;
  /**
   * Lets the caller resolve the multi-open-competition case by letting the
   * user pick one — e.g. `onClick={() => setActiveEventId(c.id)}` in the
   * `openComps` picker UI. Setting this clears `openComps` so the UI moves
   * straight to the resolved state.
   */
  setActiveEventId: (id: string) => void;
}

export interface UseActiveCompetitionOptions {
  /**
   * 'checkIn' resolves by the sign-on window (`deriveLifecycle(...).signOnOpen`),
   * for SmartCheckInPage. 'catch' resolves by the fishing window
   * (`deriveLifecycle(...).catchesOpen`), for CatchLandingPage (#113 c).
   */
  purpose: 'checkIn' | 'catch';
}

/** The next-upcoming test, by purpose: whichever bound opens the window. */
function opensAtFor(purpose: 'checkIn' | 'catch', facts: any, now: Date): number | null {
  const l = deriveLifecycle(facts, now);
  const at = purpose === 'catch' ? l.startsAt : l.signOnOpensAt;
  return at ? at.getTime() : null;
}

/**
 * Resolves which competition a check-in/catch-submission flow should act
 * against, without ever asking the user to pick one in the common case.
 *
 * Extracted from SmartCheckInPage's original inline resolve() so both
 * SmartCheckInPage (check-in) and CatchLandingPage (catch submission) share
 * one lookup/branching implementation instead of maintaining two copies.
 *
 * #134: the open/not-open test is now the single `deriveLifecycle` function
 * (sign-on window for 'checkIn', fishing window for 'catch') — no stored
 * stored competition state, no manual check-in override, no local
 * window arithmetic.
 *
 * Branches (exactly as SmartCheckInPage behaved before this extraction):
 * - Exactly one competition open for this purpose -> activeEventId is set,
 *   openComps/nextComp stay empty/null. This is the normal, most common case.
 * - More than one open -> openComps is set (sorted by date ascending); the
 *   caller should render a minimal picker.
 * - Nothing open, but one is scheduled in the future -> nextComp is set.
 * - Nothing at all -> everything stays empty/null.
 */
export function useActiveCompetition({ purpose }: UseActiveCompetitionOptions): UseActiveCompetitionResult {
  const [loading, setLoading] = useState(true);
  const [openComps, setOpenComps] = useState<OpenCompetition[]>([]);
  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const [nextComp, setNextComp] = useState<UpcomingCompetition | null>(null);

  useEffect(() => {
    const resolve = async () => {
      try {
        let comps: any[] = [];
        let nextToken: string | null = null;
        do {
          const result: any = await client.models.Event.list({
            filter: { eventType: { eq: 'COMPETITION' }, isActive: { eq: true } },
            ...(nextToken ? { nextToken } : {}),
          });
          comps = comps.concat(result.data || []);
          nextToken = result.nextToken ?? null;
        } while (nextToken);
        const now = new Date();
        const isOpenNow = (c: any) =>
          purpose === 'catch' ? deriveLifecycle(c, now).catchesOpen : deriveLifecycle(c, now).signOnOpen;
        const open = comps.filter(isOpenNow);
        if (open.length === 1) {
          setActiveEventId(open[0].id);
        } else if (open.length > 1) {
          setOpenComps(open.sort((a: any, b: any) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()));
        } else {
          const nowMs = now.getTime();
          const upcoming = comps
            .filter((c: any) => {
              const opensAt = opensAtFor(purpose, c, now);
              return opensAt !== null && opensAt > nowMs;
            })
            .sort((a: any, b: any) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
          if (upcoming.length > 0) setNextComp(upcoming[0]);
        }
      } catch (err) {
        console.error('useActiveCompetition error:', err);
      } finally {
        setLoading(false);
      }
    };
    resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purpose]);

  return {
    loading,
    openComps,
    activeEventId,
    nextComp,
    setActiveEventId: (id: string) => {
      setActiveEventId(id);
      setOpenComps([]);
    },
  };
}
